import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { usePendingReports } from '@/features/admin/hooks/useAdmin';
import { ADMIN_COLORS, staggerContainer, staggerItem } from '@/features/admin/utils/adminAnimations';
import type { WidgetProps } from '../widgetRegistry';

export function AdminPendingApprovalsWidget({ height }: WidgetProps) {
  const { t } = useTranslation();
  const { data: pendingData, isLoading } = usePendingReports();
  const compact = height < 200;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
      </div>
    );
  }

  if (!pendingData || pendingData.reports.length === 0) {
    return (
      <p className="text-center py-8 text-sm" style={{ color: ADMIN_COLORS.textTertiary }}>
        {t('admin.noPendingReports')}
      </p>
    );
  }

  return (
    <div>
      <motion.div className={compact ? 'space-y-1' : 'space-y-2'} variants={staggerContainer} initial="hidden" animate="visible">
        {pendingData.reports.slice(0, 5).map((report) => (
          <motion.div
            key={report.id}
            variants={staggerItem}
            className={`flex items-center justify-between rounded-xl ${compact ? 'p-2' : 'p-3'}`}
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-xs"
                style={{ backgroundColor: ADMIN_COLORS.primarySoft }}
              >
                {report.reportType === 'treatment' ? '\u{1F489}' : '\u{1F4D6}'}
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: ADMIN_COLORS.navy }}>
                  {report.therapist.firstName} {report.therapist.lastName}
                </p>
                {!compact && (
                  <p className="text-[10px]" style={{ color: ADMIN_COLORS.textTertiary }}>
                    {report.hospital.name} · {report.date}
                  </p>
                )}
              </div>
            </div>
            <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.primaryText }}>
              {`\u00D7${report.quantity || 1}`}
            </p>
          </motion.div>
        ))}
      </motion.div>

      {pendingData.total > 5 && (
        <Link
          to="/admin/approvals"
          className="block mt-3 text-center text-xs font-medium transition-colors"
          style={{ color: ADMIN_COLORS.primary }}
          onMouseEnter={(e) => (e.currentTarget.style.color = ADMIN_COLORS.primaryText)}
          onMouseLeave={(e) => (e.currentTarget.style.color = ADMIN_COLORS.primary)}
        >
          {t('admin.viewAll')} ({pendingData.total})
        </Link>
      )}
    </div>
  );
}
